import React from 'react';
import { Box, Typography, Tooltip, Slider } from '@mui/material';
import InfoIcon from '@mui/icons-material/Info';
import dropdownIcon from '../../../../../assets/Admin/Accordion/dropdown-icon.svg';
import './Creative.css';
import {
  CardSkeleton,
  TableSkeleton,
} from '../../../../../components/Admin/SkeletonLoader/SkeletonLoader';
import { motion, AnimatePresence } from 'framer-motion';

// Utility function to convert Koins to Riyals using dynamic conversion rate
const koinsToRiyals = (koins, conversionRate = 7.5) => {
  return (parseFloat(koins) * conversionRate).toFixed(2);
};

const Creative = ({
  creativeArtsData,
  setCreativeArtsData,
  isLoadingCreativeArts,
  handleSaveCreativeArts,
  creativeArtsAccordionData,
  loadingCreativeArtsAccordion,
  creativeArtsExpanded,
  handleCreativeArtsAccordionClick,
  handleCreativeArtsEdit,
  tooltipContent,
  conversionRate = 7.5,
  canManageFeeSettings = () => true,
}) => {
  return (
    <Box className="container">
      {/* Fee Range Slider Section */}
      <Box
        className="creative-arts-fee-range-container"
        style={{
          background: 'white',
          padding: '24px',
          borderRadius: '16px',
          maxWidth: '100%',
          overflowX: 'hidden',
          border: '1px solid #e5e7eb',
        }}
      >
        {isLoadingCreativeArts ? (
          <CardSkeleton />
        ) : (
          <>
            <Typography
              sx={{
                fontFamily: 'Poppins',
                fontSize: '16px',
                fontWeight: 600,
                color: '#111827',
                marginBottom: '20px',
              }}
            >
              Creative Arts Fee Range
              <Tooltip title={tooltipContent.feeRange}>
                <InfoIcon className="info-icon" sx={{ fontSize: '0.9rem', marginLeft: '8px' }} />
              </Tooltip>
            </Typography>

            <Box className="creative-arts-slider-section">
              <Box className="creative-arts-slider-row">
                <Box className="creative-arts-slider-left">
                  <Slider
                    value={[parseFloat(creativeArtsData.min), parseFloat(creativeArtsData.max)]}
                    onChange={(event, newValue) => {
                      setCreativeArtsData({
                        min: newValue[0].toString(),
                        max: newValue[1].toString(),
                      });
                    }}
                    valueLabelDisplay="off"
                    min={1}
                    max={100}
                    sx={{
                      width: '100%',
                      margin: '12px 0 4px 0',
                      height: '6px',
                      '& .MuiSlider-track': {
                        background: 'linear-gradient(90deg, #0a5ef3 0%, #0a5ef3 100%)',
                        border: 'none',
                        height: '6px',
                      },
                      '& .MuiSlider-rail': {
                        backgroundColor: '#e5e7eb',
                        height: '6px',
                      },
                      '& .MuiSlider-thumb': {
                        width: '20px',
                        height: '20px',
                        backgroundColor: '#0a5ef3',
                        border: '3px solid white',
                        boxShadow: '0 2px 8px rgba(59, 130, 246, 0.3)',
                        '&:hover': {
                          boxShadow: '0 4px 12px rgba(59, 130, 246, 0.4)',
                        },
                      },
                    }}
                  />
                  <Box className="creative-arts-slider-labels">
                    <span>1</span>
                    <span>100</span>
                  </Box>
                </Box>
                <button
                  className="creative-arts-save-range-button"
                  onClick={handleSaveCreativeArts}
                  disabled={isLoadingCreativeArts}
                  style={{
                    backgroundColor: '#0a5ef3',
                    color: 'white',
                    border: 'none',
                    cursor: 'pointer',
                    boxShadow: '0 2px 4px rgba(59, 130, 246, 0.2)',
                    transition: 'all 0.3s ease',
                  }}
                >
                  {isLoadingCreativeArts ? 'Saving...' : 'Save Range'}
                </button>
              </Box>
            </Box>

            {/* Current Values Display */}
            <Box className="creative-fee-range-table-container">
              <table className="creative-fee-range-table">
                <thead>
                  <tr>
                    <th className="creative-fee-range-table-header">Fee Range</th>
                    <th className="creative-fee-range-table-header">
                      Amount (Koins)
                      <Tooltip title={tooltipContent.amount}>
                        <InfoIcon className="creative-fee-range-info-icon" />
                      </Tooltip>
                    </th>
                    <th className="creative-fee-range-table-header">Amount (Riyals)</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td className="creative-fee-range-table-cell">Minimum Fee</td>
                    <td className="creative-fee-range-table-cell">
                      <span className="creative-fee-range-number-value">
                        {creativeArtsData.min}
                      </span>
                    </td>
                    <td className="creative-fee-range-table-cell">
                      <span className="creative-fee-range-number-value">
                        {koinsToRiyals(creativeArtsData.min, conversionRate)}
                      </span>
                    </td>
                  </tr>
                  <tr>
                    <td className="creative-fee-range-table-cell alternate">Maximum Fee</td>
                    <td className="creative-fee-range-table-cell alternate">
                      <span className="creative-fee-range-number-value">
                        {creativeArtsData.max}
                      </span>
                    </td>
                    <td className="creative-fee-range-table-cell alternate">
                      <span className="creative-fee-range-number-value">
                        {koinsToRiyals(creativeArtsData.max, conversionRate)}
                      </span>
                    </td>
                  </tr>
                </tbody>
              </table>
            </Box>
            {/* Note about Creative Arts fee structure */}
            <Box
              sx={{
                backgroundColor: '#fff3cd',
                border: '1px solid #ffeaa7',
                borderRadius: '8px',
                padding: '16px',
                marginTop: '16px',
              }}
            >
              <Typography
                sx={{
                  fontSize: '14px',
                  color: '#856404',
                  fontFamily: 'Poppins',
                  fontWeight: 400,
                  lineHeight: '1.5',
                  margin: 0,
                }}
              >
                <strong>Note:</strong> Use the slider above to set the fee range for all Creative
                Arts sessions. The fee breakdown structure for Creative Arts sessions follows the
                same commission and earnings model as Academic sessions. The distribution
                percentages for Service Fees, Klass Commission, and Instructor earnings remain
                consistent across all session types, with only the total fee amount varying within
                the specified range above.
              </Typography>
            </Box>
          </>
        )}
      </Box>

      {/* Creative Arts Fee Structure Accordion Section */}
      {loadingCreativeArtsAccordion ? (
        <Box sx={{ mt: 2 }}>
          <TableSkeleton />
        </Box>
      ) : (
        <Box className="creative-arts-container">
          {creativeArtsAccordionData.map((category, categoryIndex) => (
            <Box
              key={categoryIndex}
              className={`accordion ${
                creativeArtsExpanded === category.name ? 'accordion-expanded' : ''
              }`}
            >
              <Box
                className="accordion-header-creative"
                onClick={() => handleCreativeArtsAccordionClick(category.name)}
              >
                <span className="accordion-title">{category.name}</span>
                <img
                  src={dropdownIcon || '/placeholder.svg'}
                  alt="Dropdown Icon"
                  className={`chevron-icon ${creativeArtsExpanded === category.name ? 'rotate' : ''}`}
                />
              </Box>

              <AnimatePresence initial={false}>
                {creativeArtsExpanded === category.name && (
                  <motion.div
                    key="creative-content"
                    initial={{ height: 0, opacity: 0 }}
                    animate={{ height: 'auto', opacity: 1 }}
                    exit={{ height: 0, opacity: 0 }}
                    transition={{ duration: 0.25, ease: 'easeInOut' }}
                    style={{ overflow: 'hidden' }}
                  >
                    <Box className="creative-table-wrapper">
                      <table className="creative-fee-table">
                        <thead>
                          <tr className="creative-table-header-row">
                            <th className="creative-table-header creative-session-type-header">
                              Type of Session
                            </th>
                            <th className="creative-table-header creative-klass-header" colSpan="5">
                              <div className="creative-header-content">
                                Klass Commission
                                <Tooltip title={tooltipContent.klassCommission}>
                                  <InfoIcon className="creative-info-icon" />
                                </Tooltip>
                              </div>
                            </th>
                            <th
                              className="creative-table-header creative-service-header"
                              colSpan="5"
                            >
                              <div className="creative-header-content">
                                Service Fee
                                <Tooltip title={tooltipContent.platformFees}>
                                  <InfoIcon className="creative-info-icon" />
                                </Tooltip>
                              </div>
                            </th>
                            <th
                              className="creative-table-header creative-instructor-header"
                              colSpan="5"
                            >
                              <div className="creative-header-content">
                                Instructor
                                <Tooltip title={tooltipContent.teacher}>
                                  <InfoIcon className="creative-info-icon" />
                                </Tooltip>
                              </div>
                            </th>
                            <th className="creative-table-header creative-actions-header">
                              Actions
                            </th>
                          </tr>
                          <tr className="creative-table-subheader-row">
                            <th className="creative-subheader creative-session-type-subheader" />
                            <th className="creative-subheader">Min Koins</th>
                            <th className="creative-subheader">Max Koins</th>
                            <th className="creative-subheader">Min Riyals</th>
                            <th className="creative-subheader">Max Riyals</th>
                            <th className="creative-subheader">%</th>
                            <th className="creative-subheader">Min Koins</th>
                            <th className="creative-subheader">Max Koins</th>
                            <th className="creative-subheader">Min Riyals</th>
                            <th className="creative-subheader">Max Riyals</th>
                            <th className="creative-subheader">%</th>
                            <th className="creative-subheader">Min Koins</th>
                            <th className="creative-subheader">Max Koins</th>
                            <th className="creative-subheader">Min Riyals</th>
                            <th className="creative-subheader">Max Riyals</th>
                            <th className="creative-subheader">%</th>
                            <th className="creative-subheader creative-actions-subheader" />
                          </tr>
                        </thead>
                        <tbody>
                          {category.sessions.map((session, sessionIndex) => (
                            <tr
                              key={sessionIndex}
                              className={`creative-table-row ${
                                sessionIndex % 2 === 1 ? 'creative-alternate-row' : ''
                              }`}
                            >
                              <td className="creative-table-cell creative-session-type-cell">
                                {session.type}
                              </td>
                              <td className="creative-table-cell creative-klass-min-koins-cell">
                                {session.klassCom.koins}
                              </td>
                              <td className="creative-table-cell creative-klass-max-koins-cell">
                                {session.klassCom.maxKoins ||
                                  (
                                    (parseFloat(creativeArtsData.max) *
                                      session.klassCom.percentage) /
                                    100
                                  ).toFixed(2)}
                              </td>
                              <td className="creative-table-cell creative-klass-min-riyals-cell">
                                {koinsToRiyals(session.klassCom.koins, conversionRate)}
                              </td>
                              <td className="creative-table-cell creative-klass-max-riyals-cell">
                                {koinsToRiyals(
                                  session.klassCom.maxKoins ||
                                    (
                                      (parseFloat(creativeArtsData.max) *
                                        session.klassCom.percentage) /
                                      100
                                    ).toFixed(2),
                                  conversionRate
                                )}
                              </td>
                              <td className="creative-table-cell creative-klass-percentage-cell">
                                {session.klassCom.percentage}
                              </td>
                              <td className="creative-table-cell creative-service-min-koins-cell">
                                {session.cutout.koins}
                              </td>
                              <td className="creative-table-cell creative-service-max-koins-cell">
                                {session.cutout.maxKoins ||
                                  (
                                    (parseFloat(creativeArtsData.max) * session.cutout.percentage) /
                                    100
                                  ).toFixed(2)}
                              </td>
                              <td className="creative-table-cell creative-service-min-riyals-cell">
                                {koinsToRiyals(session.cutout.koins, conversionRate)}
                              </td>
                              <td className="creative-table-cell creative-service-max-riyals-cell">
                                {koinsToRiyals(
                                  session.cutout.maxKoins ||
                                    (
                                      (parseFloat(creativeArtsData.max) *
                                        session.cutout.percentage) /
                                      100
                                    ).toFixed(2),
                                  conversionRate
                                )}
                              </td>
                              <td className="creative-table-cell creative-service-percentage-cell">
                                {session.cutout.percentage}
                              </td>
                              <td className="creative-table-cell creative-instructor-min-koins-cell">
                                {session.teacher.koins}
                              </td>
                              <td className="creative-table-cell creative-instructor-max-koins-cell">
                                {session.teacher.maxKoins ||
                                  (
                                    (parseFloat(creativeArtsData.max) *
                                      session.teacher.percentage) /
                                    100
                                  ).toFixed(2)}
                              </td>
                              <td className="creative-table-cell creative-instructor-min-riyals-cell">
                                {koinsToRiyals(session.teacher.koins, conversionRate)}
                              </td>
                              <td className="creative-table-cell creative-instructor-max-riyals-cell">
                                {koinsToRiyals(
                                  session.teacher.maxKoins ||
                                    (
                                      (parseFloat(creativeArtsData.max) *
                                        session.teacher.percentage) /
                                      100
                                    ).toFixed(2),
                                  conversionRate
                                )}
                              </td>
                              <td className="creative-table-cell creative-instructor-percentage-cell">
                                {session.teacher.percentage}
                              </td>
                              <td className="creative-table-cell creative-actions-cell">
                                <button
                                  className="creative-edit-button"
                                  onClick={() =>
                                    handleCreativeArtsEdit(categoryIndex, sessionIndex)
                                  }
                                >
                                  Edit
                                </button>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </Box>
                  </motion.div>
                )}
              </AnimatePresence>
            </Box>
          ))}
        </Box>
      )}
    </Box>
  );
};

export default Creative;
